import { IListData, IErrorData } from 'src/interface';
import { currentMilisecords } from 'src/utils/time';
import { STATE_DEFAULT } from './index';
import { ArrayUtils, Logger } from 'src/utils';

export interface IListState<T> extends IErrorData {
    pageSize: number;
    currentPage: number;
    filters: {};
    isFetching: boolean;
    items: T[];
    total: number;
    hasMore: boolean;
    isPenultimate?: boolean;
    sectionId?: string;
    refresh: number;
}

const SET_LOADING = 'setLoading';
const FETCH_SUCCESS = 'fetchSuccess';
const FETCH_ERROR = 'fetchError';
const SET_CURRENT_PAGE = 'setCurrentPage';
const SET_TOTAL = 'setTotal';
const REMOVE_ITEM = 'removeItem';
const ADD_ITEM = 'addItem';
const CLEAR_ITEMS = 'clearItems';
const RESET = 'reset';
const REFRESH = 'refresh';
const SET_FILTERS = 'setFilters';

export const listActions = {
    setLoadingAction: (isFetching: boolean) => ({
        type: SET_LOADING,
        payload: isFetching,
    }),
    fetchSuccessAction: <T>(payload: IListData<T>) => ({
        type: FETCH_SUCCESS,
        payload,
    }),
    fetchErrorAction: (payload: any) => ({
        type: FETCH_ERROR,
        payload,
    }),
    setCurrentPageAction: (currentPage: number) => ({
        type: SET_CURRENT_PAGE,
        payload: currentPage,
    }),
    setTotalAction: (total: number) => ({
        type: SET_TOTAL,
        payload: total,
    }),
    removeItemAction: (item: object, keyId: string) => ({
        type: REMOVE_ITEM,
        payload: { item, keyId },
    }),
    addItemAction: (item: object, index: number) => ({
        type: ADD_ITEM,
        payload: { item, index },
    }),
    clearItems: () => ({
        type: CLEAR_ITEMS,
    }),
    setFiltersAction: (filters: any = {}) => ({
        type: SET_FILTERS,
        payload: filters,
    }),
    reset: (initState: any) => ({
        type: RESET,
        payload: initState,
    }),
    refresh: (initState: any) => ({
        type: REFRESH,
        payload: initState,
    }),
};

const listHandler: any = {
    setLoading: <T>(
        state: IListState<T>,
        isFetching: boolean
    ): IListState<T> => {
        return {
            ...state,
            isFetching,
        };
    },
    fetchSuccess: <T>(
        state: IListState<T>,
        payload: IListData<T>
    ): IListState<T> => {
        if (!payload || !payload.items) {
            return Object.assign({}, state, {
                isFetching: false,
                hasMore: payload?.hasMore,
            });
        }
        const items = ArrayUtils.uniqBy([...state.items, ...payload.items]);
        return Object.assign({}, state, {
            isFetching: false,
            items: items,
            sectionId: payload.sectionId,
            total: payload.total,
            hasMore: payload.hasMore
                ? payload.hasMore
                : payload.items.length < payload.total,
        });
    },
    fetchError: <T>(
        state: IListState<T>,
        payload: IListState<T>
    ): IListState<T> => {
        return Object.assign({}, state, {
            isFetching: false,
            hasMore: false,
            err: payload.err,
        });
    },
    setCurrentPage: <T>(
        state: IListState<T>,
        currentPage: number
    ): IListState<T> => {
        Logger.debug('useList | setCurrentPage', currentPage);
        return {
            ...state,
            currentPage,
        };
    },
    setTotal: <T>(state: IListState<T>, total: number): IListState<T> => {
        return {
            ...state,
            total,
        };
    },
    setFilters: <T>(state: IListState<T>, filters: any): IListState<T> => {
        return {
            ...state,
            ...STATE_DEFAULT,
            filters,
        };
    },
    addItem: <T>(state: IListState<T>, { item }: { item: any }): IListState<T> => {
        Logger.debug('useList | addItem', item);
        return {
            ...state,
            items: { ...state.items, ...item },
            total: state.total + 1,
        };
    },
    removeItem: <T>(state: IListState<T>, { item, keyId }: { item: any, keyId: any }): IListState<T> => {
        Logger.debug('useList | removeItem', item);
        return {
            ...state,
            items: [...state.items].filter(
                (listItem: any) => listItem[keyId] !== item[keyId]
            ),
            total: state.total - 1,
        };
    },
    clearItems: <T>(state: IListState<T>): IListState<T> => {
        Logger.debug('useList | clearItems');
        return {
            ...state,
            items: [],
        };
    },
    reset: <T>(
        state: IListState<T>,
        initState = STATE_DEFAULT
    ): IListState<T> => {
        Logger.debug('useList | reset');
        return {
            ...state,
            ...initState,
            filters: state.filters,
        };
    },
    refresh: <T>(
        state: IListState<T>,
        initState = STATE_DEFAULT
    ): IListState<T> => {
        Logger.debug('useList | refresh');
        return {
            ...state,
            ...initState,
            refresh: currentMilisecords(),
        };
    },
};

export function listReducer<T>(state: IListState<T>, action: any) {
    if (listHandler[action.type]) {
        Logger.debug(
            'action.type | action.payload ',
            action.type,
            action.payload
        );
        return listHandler[action.type](state, action.payload);
    }

    return { ...state };
}
